import { useState } from 'react';
import { ChevronDown } from 'lucide-react';

interface SSOTAuditTrailProps {
  parameters: string[];
  calculatedAt?: string;
}

/** Group SSOT parameter names by prefix (loan_, expense_, cap_, tax_, etc.) */
function groupParameters(params: string[]): Record<string, string[]> {
  const groups: Record<string, string[]> = {};
  for (const p of params) {
    const idx = p.indexOf('_');
    const prefix = idx > 0 ? p.slice(0, idx) : 'other';
    if (!groups[prefix]) groups[prefix] = [];
    groups[prefix].push(p);
  }
  // Sort groups alphabetically
  const sorted: Record<string, string[]> = {};
  for (const key of Object.keys(groups).sort()) {
    sorted[key] = groups[key].sort();
  }
  return sorted;
}

function prefixLabel(prefix: string): string {
  const labels: Record<string, string> = {
    loan: 'Loan',
    expense: 'Expense',
    cap: 'Cap Rate',
    tax: 'Tax',
    rent: 'Rent',
    vacancy: 'Vacancy',
    growth: 'Growth',
    construction: 'Construction',
    exit: 'Exit',
    depreciation: 'Depreciation',
  };
  return labels[prefix] || prefix.charAt(0).toUpperCase() + prefix.slice(1);
}

export function SSOTAuditTrail({ parameters, calculatedAt }: SSOTAuditTrailProps) {
  const [isOpen, setIsOpen] = useState(false);

  if (!parameters || parameters.length === 0) return null;

  const grouped = groupParameters(parameters);

  return (
    <div className="mt-3">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1.5 text-sm text-stone-700 hover:text-stone-700 transition-colors cursor-pointer"
      >
        <ChevronDown
          size={10}
          className={`transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
        />
        <span>Show SSOT Audit Trail</span>
        <span className="text-stone-700">({parameters.length} params)</span>
        {calculatedAt && (
          <span className="text-stone-700 ml-1">
            &middot; {new Date(calculatedAt).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
          </span>
        )}
      </button>

      <div
        className={`grid transition-all duration-200 ease-in-out ${
          isOpen ? 'grid-rows-[1fr] opacity-100 mt-2' : 'grid-rows-[0fr] opacity-0'
        }`}
      >
        <div className="overflow-hidden">
          <div className="bg-charcoal/40 border border-slate-dark rounded-lg p-3 space-y-2">
            {Object.entries(grouped).map(([prefix, params]) => (
              <div key={prefix}>
                <span className="text-sm font-semibold uppercase tracking-wider text-stone-700">
                  {prefixLabel(prefix)}
                </span>
                <div className="flex flex-wrap gap-1 mt-0.5">
                  {params.map((param) => (
                    <span
                      key={param}
                      className="text-sm font-mono bg-charcoal border border-slate-dark text-stone-700 px-1.5 py-0.5 rounded"
                    >
                      {param}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
